"use client";

import type { CSSProperties, RefObject } from "react";
import { useLayoutEffect, useState } from "react";

interface UseParallaxTextScrollRangeInput {
  contentRef: RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  parallaxTextSize: number;
  scrollRef: RefObject<HTMLDivElement | null>;
  shouldRenderContent: boolean;
  textBottomSpacing: CSSProperties["paddingBottom"] | undefined;
  textTopSpacing: CSSProperties["paddingTop"] | undefined;
  vh: number;
}

export function useParallaxTextScrollRange({
  contentRef,
  isMobile,
  parallaxTextSize,
  scrollRef,
  shouldRenderContent,
  textBottomSpacing,
  textTopSpacing,
  vh,
}: UseParallaxTextScrollRangeInput): number {
  const [parallaxTextScrollRange, setParallaxTextScrollRange] = useState(0);

  useLayoutEffect(() => {
    if (!shouldRenderContent) return;

    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const updateScrollRange = (): void => {
      const nextScrollRange = Math.max(
        0,
        scrollElement.scrollHeight - scrollElement.clientHeight,
      );
      setParallaxTextScrollRange((currentScrollRange) => {
        return currentScrollRange === nextScrollRange
          ? currentScrollRange
          : nextScrollRange;
      });
    };

    updateScrollRange();

    const resizeObserver = new ResizeObserver(updateScrollRange);
    resizeObserver.observe(scrollElement);

    const contentElement = contentRef.current;
    if (contentElement) {
      resizeObserver.observe(contentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    contentRef,
    isMobile,
    parallaxTextSize,
    scrollRef,
    shouldRenderContent,
    textBottomSpacing,
    textTopSpacing,
    vh,
  ]);

  return parallaxTextScrollRange;
}
